/**
 * Re-draw a photo before it is uploaded (v9.0.0).
 *
 * WHY: a phone photo carries metadata — on an iPhone, by default, WHERE it was
 * taken (EXIF GPS), plus the camera and the time. None of that is the receipt.
 * Drawing the picture onto a canvas and encoding it fresh keeps the pixels and
 * drops everything else (ASVS 5.0 V14.2.8), and it is the "image re-writing"
 * the OWASP File Upload guidance recommends: whatever else was hiding in the
 * file does not survive being redrawn. It also turns an iPhone HEIC into a JPEG
 * any browser can show, and caps the size (lib/receipt-copy.ts `fitWithin`).
 *
 * PDFs are sent as they are; the server checks them.
 *
 * IF THE BROWSER CANNOT DECODE THE FILE, it is sent unchanged and the server
 * decides — it refuses anything that is not a photo or a PDF. That fallback is
 * reported (`rewritten: false`) rather than hidden.
 */

import { fitWithin } from './receipt-copy';

function decode(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('undecodable'));
    };
    img.src = url;
  });
}

export async function prepareForUpload(file: File): Promise<{ blob: Blob; rewritten: boolean }> {
  if (!file.type.startsWith('image/')) return { blob: file, rewritten: false };
  try {
    const img = await decode(file);
    if (!img.naturalWidth || !img.naturalHeight) return { blob: file, rewritten: false };
    const size = fitWithin(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { blob: file, rewritten: false };
    // A screenshot stays lossless; a photo becomes a JPEG.
    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    if (type === 'image/jpeg') {
      // JPEG has no transparency: paint white under it rather than black.
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, size.width, size.height);
    }
    ctx.drawImage(img, 0, 0, size.width, size.height);
    const blob = await new Promise<Blob | null>((done) => canvas.toBlob(done, type, 0.9));
    // Release the canvas's memory now: on iOS it counts against a small budget.
    canvas.width = 0;
    canvas.height = 0;
    return blob ? { blob, rewritten: true } : { blob: file, rewritten: false };
  } catch {
    return { blob: file, rewritten: false };
  }
}
